import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useT } from '../../lib/i18n';

// Single-line summary tile pointing the user to the Clients tab where
// the full at-risk list lives. Mirrors the demo's design decision to
// keep at-risk discoverability here without duplicating the row-by-row
// list. Visual state shifts based on count:
//   0  → green "all clear"
//   1+ → red "review now" with the count
//
// Acknowledgement: the left side of the tile is a checkbox the barber
// can tap to mark the current at-risk set "reviewed." The tile then
// stays hidden until the set changes (additions OR removals OR going
// from empty → non-empty, etc.). Persistence is a localStorage hash
// of the sorted at-risk client IDs — when the live hash matches the
// stored one, we hide; when they differ, we resurface the tile.
//
// The "all clear" state works the same way: tap the box → tile hides
// → reappears if anyone slips into at-risk later.

const ACK_STORAGE_KEY = 'busy.atRiskSummary.ackedHash';

function readAckedHash(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(ACK_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeAckedHash(hash: string) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(ACK_STORAGE_KEY, hash);
  } catch {
    /* quota / private mode — tile re-shows next paint, acceptable */
  }
}

/** Stable hash for the current at-risk set. Sort first so order of
 *  the input doesn't matter; JSON-stringify so the empty set has its
 *  own canonical representation ("[]") distinct from "never acked". */
function hashIds(ids: readonly string[]): string {
  return JSON.stringify([...ids].sort());
}

export function AtRiskSummaryTile({
  count,
  clientIds,
}: {
  count: number;
  /** Sorted-or-not list of at-risk client IDs. The tile derives its
   *  hash internally; pass the full list for the empty / non-empty
   *  hash to be distinguishable. */
  clientIds: readonly string[];
}) {
  const navigate = useNavigate();
  const t = useT();
  const overdue = count > 0;

  const currentHash = useMemo(() => hashIds(clientIds), [clientIds]);
  const [ackedHash, setAckedHash] = useState<string | null>(() => readAckedHash());

  // Hide entirely when the live set matches what the user last marked
  // reviewed. Any change (add / remove / empty toggle) flips the hash
  // and surfaces the tile again.
  if (ackedHash === currentHash) return null;

  function handleAck(e: React.MouseEvent | React.KeyboardEvent) {
    e.stopPropagation();
    e.preventDefault();
    writeAckedHash(currentHash);
    setAckedHash(currentHash);
  }

  // Two interactive regions so we don't nest <button> inside <button>:
  //   - Left "reviewed" checkbox     → acks the current set, hides tile
  //   - Right navigation button area → routes to /clients
  return (
    <div
      className={`rounded-2xl border flex items-stretch overflow-hidden transition-colors ${
        overdue
          ? 'border-red-300 dark:border-red-600/40 bg-red-50 dark:bg-red-600/15'
          : 'border-green-200 dark:border-green-600/40 bg-green-50 dark:bg-green-600/15'
      }`}
    >
      <button
        type="button"
        onClick={handleAck}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') handleAck(e);
        }}
        aria-label={t(
          overdue ? 'today.atRisk.ackAriaOverdue' : 'today.atRisk.ackAriaClear',
        )}
        className={`shrink-0 grid place-items-center w-16 transition-colors ${
          overdue
            ? 'bg-red-600 hover:bg-red-700 text-white'
            : 'bg-green-600 hover:bg-green-700 text-white'
        }`}
      >
        <CheckboxIcon className="w-7 h-7" />
      </button>
      <button
        type="button"
        onClick={() => navigate('/clients')}
        className={`flex-1 text-left flex items-center gap-3 px-4 py-3 transition-colors ${
          overdue
            ? 'hover:bg-red-100 dark:hover:bg-red-600/25'
            : 'hover:bg-green-100 dark:hover:bg-green-600/25'
        }`}
      >
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.16em] uppercase font-extrabold text-ink-500">
            {t('dash.atRisk.eyebrow')}
          </div>
          <div className="flex items-baseline gap-2 mt-0.5">
            <div
              className={`font-display text-3xl tabular-nums ${
                overdue ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-300'
              }`}
            >
              {count}
            </div>
            <div
              className={`text-sm font-semibold ${
                overdue ? 'text-red-700 dark:text-red-300' : 'text-green-700 dark:text-green-200'
              }`}
            >
              {overdue
                ? t(count === 1 ? 'dash.atRisk.overdue_one' : 'dash.atRisk.overdue_other')
                : t('dash.atRisk.allClear')}
            </div>
          </div>
        </div>
        <ChevronIcon
          className={`w-5 h-5 shrink-0 ${
            overdue ? 'text-red-500 dark:text-red-400' : 'text-green-500 dark:text-green-300'
          }`}
        />
      </button>
    </div>
  );
}

/** Empty square + accessible label. Tapping the parent button marks
 *  the current at-risk set as reviewed (the tile then disappears), so
 *  the icon never needs to render in a "checked" state — disappearing
 *  IS the checked state. */
function CheckboxIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="4" y="4" width="16" height="16" rx="3" />
    </svg>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="9 6 15 12 9 18" />
    </svg>
  );
}
